import { Routes, Route, Navigate } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import BooksList from "./pages/BooksList";
import BookDetails from "./pages/BookDetails";
import BookForm from "./pages/BookForm";
import UsersList from "./pages/UsersList";
import UserDetails from "./pages/UserDetails";
import UserForm from "./pages/UserForm";
import UserSubscriptionsList from "./pages/UserSubscriptionsList";
import UserSubscriptionDetails from "./pages/UserSubscriptionDetails";
import SubscriptionPlansList from "./pages/SubscriptionPlansList";
import SubscriptionPlanForm from "./pages/SubscriptionPlanForm";
import PromotionsList from "./pages/PromotionsList";
import NotFound from "./pages/NotFound";
import CategoriesList from "@/pages/CategoriesList";
import CategoryForm from "@/pages/CategoryForm";
import LanguagesList from "@/pages/LanguagesList";
import LanguageForm from "@/pages/LanguageForm";
import PeopleList from "@/pages/PeopleList";
import PersonForm from "@/pages/PersonForm";
import PersonDetails from "@/pages/PersonDetails";
import PeopleRoleForm from "@/pages/PeopleRoleForm";
import PeopleRolesList from "./pages/PeopleRolesList";
import UserRolesList from "./pages/UserRolesList";
import UserRoleForm from "./pages/UserRoleForm";
import MediaLibrary from "./pages/MediaLibrary";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import StandardUserRedirectPage from "./pages/StandardUserRedirectPage";
import InitialSetupPage from "./pages/InitialSetupPage";
import InvitationRedirect from "./pages/InvitationRedirect";
import AuthGuard from "./components/auth/AuthGuard";
import CollectionsList from "./pages/CollectionsList";
import CollectionDetails from "./pages/CollectionDetails";
import CollectionForm from "./pages/CollectionForm";
import SectionsList from "./pages/SectionsList";
import SectionDetails from "./pages/SectionDetails";
import SectionForm from "./pages/SectionForm";
import ProfilePage from "./pages/ProfilePage";
import PromotionForm from "./pages/PromotionForm";
import PaymentMethodsList from "@/pages/PaymentMethodsList";
import PaymentMethodForm from "@/pages/PaymentMethodForm";
import CurrenciesList from "@/pages/CurrenciesList";
import CollaboratorDashboard from "./pages/CollaboratorDashboard";
import CollaboratorsList from "./pages/CollaboratorsList";
import CollaboratorEarningsList from "./pages/CollaboratorEarningsList";
import CollaboratorPayoutsList from "./pages/CollaboratorPayoutsList";
import ReferralRewardsList from "./pages/ReferralRewardsList";
import ReferralStatsList from "./pages/ReferralStatsList";
import ContentAnalyticsDashboard from "./pages/ContentAnalyticsDashboard";
import UserAnalyticsDashboard from "./pages/UserAnalyticsDashboard";
import BusinessAnalyticsDashboard from "./pages/BusinessAnalyticsDashboard";
import SystemAnalyticsDashboard from "./pages/SystemAnalyticsDashboard";
import DataImport from "./pages/DataImport";
import UserGroupsList from "./pages/UserGroupsList";
import UserGroupForm from "./pages/UserGroupForm";
import UserGroupDetails from "./pages/UserGroupDetails";
import NotificationTemplatesList from "./pages/NotificationTemplatesList";
import NotificationTemplateForm from "./pages/NotificationTemplateForm";
import NotificationAutomationList from "./pages/NotificationAutomationList";
import NotificationAutomationForm from "./pages/NotificationAutomationForm";
import NotificationCampaignsList from "./pages/NotificationCampaignsList";
import NotificationCampaignForm from "./pages/NotificationCampaignForm";
import NotificationAnalytics from "./pages/NotificationAnalytics";
import NotificationDashboard from "./pages/NotificationDashboard";
import NotificationCleanup from "./pages/NotificationCleanup";
import SendNotification from "./pages/SendNotification";
import AccountDeletionFeedback from "./pages/AccountDeletionFeedback";
import UserBookFeedback from "./pages/UserBookFeedback";
import SystemSettings from "./pages/SystemSettings";
import BetaTestersPage from "./pages/BetaTestersPage";
import UserFeedbackList from "./pages/UserFeedbackList";
import AIChatSessionsList from "./pages/AIChatSessionsList";
import AIChatSessionDetail from "./pages/AIChatSessionDetail";
import AiFeedbackAnalytics from "./pages/AiFeedbackAnalytics";
import AIChatPlayground from "./pages/AIChatPlayground";
import AiPromptTemplatesList from "./pages/AiPromptTemplatesList";
import AiPromptTemplateForm from "./pages/AiPromptTemplateForm";
import AiPersonasList from "./pages/AiPersonasList";
import AiPersonaForm from "./pages/AiPersonaForm";
import AiPersonaDetail from "./pages/AiPersonaDetail";
import UserAiProfilesList from "./pages/UserAiProfilesList";
import UserAiProfileDetail from "./pages/UserAiProfileDetail";
import AiKnowledgeBase from "./pages/AiKnowledgeBase";
import AiCreditConfig from "./pages/AiCreditConfig";
import AiUsageAnalytics from "./pages/AiUsageAnalytics";
import AiHealthDashboard from "./pages/AiHealthDashboard";
import OnboardingFlowsList from "./pages/OnboardingFlowsList";
import OnboardingFlowForm from "./pages/OnboardingFlowForm";
import ReadingMilestonesList from "./pages/ReadingMilestonesList";
import ReadingMilestoneForm from "./pages/ReadingMilestoneForm";

type AppRoutesProps = {
  setupCompleted: boolean | null;
};

const AppRoutes = ({ setupCompleted }: AppRoutesProps) => {
  // Show loading state while checking setup status
  if (setupCompleted === null) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />

      {/* ===== Auth Routes ===== */}
      <Route
        path="/admin/setup"
        element={
          setupCompleted ? (
            <Navigate to="/admin/login" replace />
          ) : (
            <InitialSetupPage />
          )
        }
      />
      <Route path="/admin/login" element={<LoginPage />} />
      <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/admin/standard-user"
        element={<StandardUserRedirectPage />}
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/invite" element={<InvitationRedirect />} />
      <Route path="/auth/v1/verify" element={<InvitationRedirect />} />
      <Route path="/verify" element={<InvitationRedirect />} />

      {/* ===== Dashboard ===== */}
      <Route
        path="/admin/dashboard"
        element={
          <AuthGuard>
            <Dashboard />
          </AuthGuard>
        }
      />

      {/* ===== Analytics Dashboards ===== */}
      <Route
        path="/admin/analytics/content"
        element={
          <AuthGuard>
            <ContentAnalyticsDashboard />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/analytics/users"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <UserAnalyticsDashboard />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/analytics/business"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <BusinessAnalyticsDashboard />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/analytics/system"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <SystemAnalyticsDashboard />
          </AuthGuard>
        }
      />

      {/* ===== Data Import ===== */}
      <Route
        path="/admin/data-import"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <DataImport />
          </AuthGuard>
        }
      />

      {/* ===== Books ===== */}
      <Route
        path="/admin/books"
        element={
          <AuthGuard>
            <BooksList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/books/:id"
        element={
          <AuthGuard>
            <BookDetails />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/books/:id/edit"
        element={
          <AuthGuard>
            <BookForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/books/new"
        element={
          <AuthGuard>
            <BookForm />
          </AuthGuard>
        }
      />

      {/* ===== Collections ===== */}
      <Route
        path="/admin/collections"
        element={
          <AuthGuard>
            <CollectionsList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/collections/:id"
        element={
          <AuthGuard>
            <CollectionDetails />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/collections/:id/edit"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <CollectionForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/collections/new"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <CollectionForm />
          </AuthGuard>
        }
      />

      {/* ===== Sections ===== */}
      <Route
        path="/admin/sections"
        element={
          <AuthGuard>
            <SectionsList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/sections/new"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <SectionForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/sections/:id"
        element={
          <AuthGuard>
            <SectionDetails />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/sections/:id/edit"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <SectionForm />
          </AuthGuard>
        }
      />

      {/* ===== Users ===== */}
      <Route
        path="/admin/users"
        element={
          <AuthGuard>
            <UsersList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/users/new"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <UserForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/users/:id"
        element={
          <AuthGuard>
            <UserDetails />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/users/:id/edit"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <UserForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/account-deletion-feedback"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <AccountDeletionFeedback />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/user-book-feedback"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <UserBookFeedback />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/profile"
        element={
          <AuthGuard>
            <ProfilePage />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/beta-testing"
        element={
          <AuthGuard>
            <BetaTestersPage />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/user-subscriptions"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <UserSubscriptionsList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/user-subscriptions/:subscriptionId"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <UserSubscriptionDetails />
          </AuthGuard>
        }
      />

      {/* ===== User Groups ===== */}
      <Route
        path="/admin/user-groups"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <UserGroupsList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/user-groups/new"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <UserGroupForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/user-groups/:id"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <UserGroupDetails />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/user-groups/:id/edit"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <UserGroupForm />
          </AuthGuard>
        }
      />

      {/* ===== Notifications ===== */}
      <Route
        path="/admin/notifications"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <NotificationDashboard />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/notifications/templates"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <NotificationTemplatesList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/notifications/templates/new"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <NotificationTemplateForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/notifications/templates/:id/edit"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <NotificationTemplateForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/notifications/automation"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <NotificationAutomationList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/notifications/automation/new"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <NotificationAutomationForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/notifications/automation/:id/edit"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <NotificationAutomationForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/notifications/send"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <SendNotification />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/notifications/campaigns"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <NotificationCampaignsList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/notifications/campaigns/new"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <NotificationCampaignForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/notifications/campaigns/:id/edit"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <NotificationCampaignForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/notifications/analytics"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <NotificationAnalytics />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/notifications/cleanup"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <NotificationCleanup />
          </AuthGuard>
        }
      />

      {/* ===== Subscriptions ===== */}
      <Route
        path="/admin/subscriptions"
        element={
          <AuthGuard>
            <SubscriptionPlansList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/subscriptions/:id"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <SubscriptionPlanForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/subscriptions/new"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <SubscriptionPlanForm />
          </AuthGuard>
        }
      />

      {/* ===== Promotions ===== */}
      <Route
        path="/admin/promotions"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <PromotionsList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/promotions/new"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <PromotionForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/promotions/:id"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <PromotionForm />
          </AuthGuard>
        }
      />

      {/* ===== Reading Rewards ===== */}
      <Route
        path="/admin/reading-rewards"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <ReadingMilestonesList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/reading-rewards/new"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <ReadingMilestoneForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/reading-rewards/:id"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <ReadingMilestoneForm />
          </AuthGuard>
        }
      />

      {/* ===== Collaboration ===== */}
      <Route
        path="/admin/collaborators"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <CollaboratorsList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/collaborators/earnings"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <CollaboratorEarningsList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/collaborators/payouts"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <CollaboratorPayoutsList />
          </AuthGuard>
        }
      />

      {/* ===== Collaborator Dashboard ===== */}
      <Route
        path="/admin/collaborator/:id"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <CollaboratorDashboard />
          </AuthGuard>
        }
      />

      {/* ===== Referral Programme ===== */}
      <Route
        path="/admin/referrals/rewards"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <ReferralRewardsList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/referrals/stats"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <ReferralStatsList />
          </AuthGuard>
        }
      />

      {/* ===== Categories ===== */}
      <Route
        path="/admin/categories"
        element={
          <AuthGuard>
            <CategoriesList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/categories/new"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <CategoryForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/categories/:id/edit"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <CategoryForm />
          </AuthGuard>
        }
      />

      {/* ===== Media Library ===== */}
      <Route
        path="/admin/media"
        element={
          <AuthGuard>
            <MediaLibrary />
          </AuthGuard>
        }
      />

      {/* ===== AI Knowledge Base ===== */}
      <Route
        path="/admin/ai/knowledge-base"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AiKnowledgeBase />
          </AuthGuard>
        }
      />

      {/* ===== AI Credits ===== */}
      <Route
        path="/admin/ai/credits"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AiCreditConfig />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/ai/usage"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AiUsageAnalytics />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/ai/health"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AiHealthDashboard />
          </AuthGuard>
        }
      />

      {/* ===== Ask AI ===== */}
      <Route
        path="/admin/ask-ai"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AIChatSessionsList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/ask-ai/playground"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AIChatPlayground />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/ask-ai/:sessionId"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AIChatSessionDetail />
          </AuthGuard>
        }
      />

      <Route
        path="/admin/ai/feedback"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AiFeedbackAnalytics />
          </AuthGuard>
        }
      />

      {/* ===== AI Personas ===== */}
      <Route
        path="/admin/ai/personas"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AiPersonasList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/ai/personas/new"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AiPersonaForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/ai/personas/:id"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AiPersonaDetail />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/ai/personas/:id/edit"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AiPersonaForm />
          </AuthGuard>
        }
      />

      {/* ===== AI Prompt Templates ===== */}
      <Route
        path="/admin/ai/prompt-templates"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AiPromptTemplatesList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/ai/prompt-templates/new"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AiPromptTemplateForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/ai/prompt-templates/:id/edit"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <AiPromptTemplateForm />
          </AuthGuard>
        }
      />

      {/* ===== AI User Profiles ===== */}
      <Route
        path="/admin/ai/user-profiles"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <UserAiProfilesList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/ai/user-profiles/:id"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <UserAiProfileDetail />
          </AuthGuard>
        }
      />

      {/* ===== Mobile App - Onboarding ===== */}
      <Route
        path="/admin/onboarding"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <OnboardingFlowsList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/onboarding/new"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <OnboardingFlowForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/onboarding/:id/edit"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <OnboardingFlowForm />
          </AuthGuard>
        }
      />

      {/* ===== System Settings ===== */}
      {/* Languages */}
      <Route
        path="/admin/system/languages"
        element={
          <AuthGuard>
            <LanguagesList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/system/languages/new"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <LanguageForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/system/languages/edit/:code"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <LanguageForm />
          </AuthGuard>
        }
      />

      {/* People */}
      <Route
        path="/admin/people"
        element={
          <AuthGuard>
            <PeopleList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/people/:id"
        element={
          <AuthGuard>
            <PersonDetails />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/people/new"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <PersonForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/people/:id/edit"
        element={
          <AuthGuard requiredRoles={[1, 2, 3]}>
            <PersonForm />
          </AuthGuard>
        }
      />

      {/* People Roles */}
      <Route
        path="/admin/system/people-roles"
        element={
          <AuthGuard>
            <PeopleRolesList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/system/people-roles/new"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <PeopleRoleForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/system/people-roles/:id/edit"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <PeopleRoleForm />
          </AuthGuard>
        }
      />

      {/* User Roles */}
      <Route
        path="/admin/system/user-roles"
        element={
          <AuthGuard>
            <UserRolesList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/system/user-roles/new"
        element={
          <AuthGuard requiredRoles={[1]}>
            <UserRoleForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/system/user-roles/:id/edit"
        element={
          <AuthGuard requiredRoles={[1]}>
            <UserRoleForm />
          </AuthGuard>
        }
      />

      {/* Subscription Plans */}
      <Route
        path="/admin/subscription-plans"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <SubscriptionPlansList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/subscription-plans/new"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <SubscriptionPlanForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/subscription-plans/:id"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <SubscriptionPlanForm />
          </AuthGuard>
        }
      />

      {/* Payment Methods */}
      <Route
        path="/admin/system/payment-methods"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <PaymentMethodsList />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/system/payment-methods/new"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <PaymentMethodForm />
          </AuthGuard>
        }
      />
      <Route
        path="/admin/system/payment-methods/:id"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <PaymentMethodForm />
          </AuthGuard>
        }
      />

      {/* Currencies */}
      <Route
        path="/admin/system/currencies"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <CurrenciesList />
          </AuthGuard>
        }
      />

      {/* Beta Testing */}
      <Route
        path="/admin/beta-testers"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <BetaTestersPage />
          </AuthGuard>
        }
      />
      {/* User Feedback */}
      <Route
        path="/admin/user-feedback"
        element={
          <AuthGuard requiredRoles={[1, 2]}>
            <UserFeedbackList />
          </AuthGuard>
        }
      />

      {/* System Settings */}
      <Route
        path="/admin/system/settings"
        element={
          <AuthGuard requiredRoles={[1]}>
            <SystemSettings />
          </AuthGuard>
        }
      />

      {/* Catch All */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
